import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { ParticleBackground } from "@/components/ParticleBackground";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import Page from "@/components/layout/Page";
import { getProductImage, getProductDescription, getProductPrice } from "@/utils/productHelpers";
import { SEOHead } from "@/components/seo/SEOHead";
import { ProductSchema, BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function SensoryPads() {
  const { addItem } = useCart();
  const { toast } = useToast();
  
  // Sync data from Shop page
  const productName = "Sensory Pads";
  const productImage = getProductImage(productName);
  const productDescription = getProductDescription(productName);
  const productPrice = getProductPrice(productName);

  const handleAddToCart = () => {
    addItem({
      id: "sensory-pads",
      name: "Sensory Pads",
      slug: "/sensory-pads",
      price: productPrice,
    });
    toast({
      title: "Added to cart",
      description: "Sensory Pads has been added to your cart.",
    });
  };

  return (
    <>
      <SEOHead
        title="Sensory Pads | Premium Tactile Tools for Focus & Calm | Samboards"
        description="Handmade Sensory Pads for anxiety relief, focus improvement & motor skill development. Perfect for autism, ADHD, dementia care & workplace wellness. Made in UK."
        canonical="https://samboards.co.uk/sensory-pads"
        keywords="sensory pads, tactile sensory tools, anxiety relief pads, ADHD focus tools, autism sensory tools, dementia care tools, workplace wellness, fidget tools, motor skills, handmade UK"
        ogImage={productImage.src}
      />
      <ProductSchema
        name="Sensory Pads"
        description={productDescription}
        price={productPrice}
        image={productImage.src}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Shop", url: "/shop" },
          { name: "Sensory Pads", url: "/sensory-pads" }
        ]}
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground className="absolute inset-0 -z-10" />
      
      <Page>
        <section className="pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="group relative">
              <div className="relative rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-300 group-hover:shadow-[0_0_40px_rgba(16,185,129,0.3)] group-hover:border-emerald-400/30">
                <img
                  src={productImage.src}
                  alt={productImage.alt}
                  className="w-full h-auto rounded-2xl"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h1 className="text-[40px] sm:text-[48px] lg:text-[54px] leading-[1.1] font-bold mb-4 page-title-gradient">Samboard Sensory Pad</h1>
                <p className="text-lg text-slate-200/80 leading-relaxed">
                  {productDescription}
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-slate-100">Key Benefits</h3>
                <ul className="space-y-3">
                  {[
                    "Increase focus and concentration",
                    "Manage stress and anxiety",
                    "Break bad habits",
                    "Develop fine motor skills",
                    "Support sensory needs"
                  ].map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-base text-slate-300/80">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] space-y-4">
                <p className="text-3xl font-bold text-slate-50">£62.50</p>
                <Button 
                  asChild
                  size="lg" 
                  className="w-full bg-gradient-to-r from-emerald-400 to-teal-400 text-slate-900 hover:from-emerald-300 hover:to-teal-300 rounded-full shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_55px_rgba(16,185,129,0.5)] transition-all"
                >
                  <Link to="/checkout?product=Sensory%20Pads">Buy Now</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </Page>
    </div>
    </>
  );
}
